import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Users, RefreshCw, Loader2, Plus, Phone, Mail, Globe, Tag, X, ChevronRight, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_OPTS = [
  { id: 'called',    label: 'Llamada',    pts: '+15' },
  { id: 'messaged',  label: 'WA mensaje', pts: '+10' },
  { id: 'emailed',   label: 'Email',      pts: '+8'  },
  { id: 'meeting',   label: 'Reunión',    pts: '+25' },
  { id: 'note',      label: 'Nota',       pts: '+2'  },
];

const SCORE_COLOR = (s) => s >= 70 ? 'text-emerald-400' : s >= 40 ? 'text-amber-400' : 'text-slate-400';

const SOURCE_ICON = { formulario: Mail, llamada: Phone, chat: Users };

const fmtDate = (iso) => {
  if (!iso) return 'Sin actividad';
  try { return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

function NewContactModal({ onClose, onSave }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', source: 'manual', country: '', notes: '' });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = (e) => { e.preventDefault(); if (!form.name) return; onSave(form); };
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="glass-card w-full max-w-md p-6 space-y-4" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="text-text-heading font-semibold">Nuevo Contacto</h3><button onClick={onClose}><X className="w-4 h-4 text-text-muted" /></button></div>
        <form onSubmit={submit} className="space-y-3">
          {[['Nombre *', 'name'], ['Teléfono WA', 'phone'], ['Email', 'email'], ['País', 'country']].map(([label, key]) => (
            <div key={key}>
              <label className="text-[11px] text-text-muted uppercase tracking-wider">{label}</label>
              <input value={form[key]} onChange={e => set(key, e.target.value)} className="w-full mt-1 bg-background border border-border-subtle rounded-lg px-3 py-2 text-sm text-text-body focus:outline-none focus:border-primary" />
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onClose} className="flex-1 btn-secondary !py-2 text-sm">Cancelar</button>
            <button type="submit" className="flex-1 btn-primary !py-2 text-sm">Guardar</button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function ContactRow({ contact, onAction, onTagUpdate, expanded, onToggle }) {
  const [actionType, setActionType] = useState('called');
  const [note, setNote] = useState('');
  const [newTag, setNewTag] = useState('');
  const [busy, setBusy] = useState(false);
  const Icon = SOURCE_ICON[contact.source] || Globe;

  const logAction = async () => {
    setBusy(true);
    await onAction(contact.contact_id, actionType, note);
    setNote('');
    setBusy(false);
  };

  const addTag = () => {
    if (!newTag.trim()) return;
    onTagUpdate(contact.contact_id, [...(contact.tags || []), newTag.trim()]);
    setNewTag('');
  };

  const removeTag = (tag) => onTagUpdate(contact.contact_id, (contact.tags || []).filter(t => t !== tag));

  return (
    <div className="glass-card overflow-hidden" data-testid={`crm-contact-${contact.contact_id}`}>
      <button onClick={onToggle} className="w-full flex items-center gap-3 p-4 hover:bg-card/40 transition-colors text-left">
        <Icon className="w-4 h-4 text-text-muted flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-heading truncate">{contact.name}
            {contact.followup_needed && (
              <span className="ml-2 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/30">SEGUIMIENTO</span>
            )}
          </p>
          <p className="text-xs text-text-muted">{contact.phone || contact.email || contact.contact || '—'} · {fmtDate(contact.last_action_at)}</p>
        </div>
        <div className="flex items-center gap-3">
          {contact.country && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/10 text-primary">{contact.country}</span>}
          <span className={`text-sm font-black tabular-nums ${SCORE_COLOR(contact.score)}`}>{contact.score}</span>
          <ChevronRight className={`w-4 h-4 text-text-muted transition-transform ${expanded ? 'rotate-90' : ''}`} />
        </div>
      </button>

      {expanded && (
        <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
          className="border-t border-border-subtle p-4 space-y-3 bg-background/30">
          {/* Next action */}
          <p className="text-xs text-text-muted"><span className="font-semibold text-primary">Próxima acción:</span> {contact.next_action || '—'}</p>

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5 items-center">
            {(contact.tags || []).map(tag => (
              <span key={tag} className="flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-card border border-border-subtle text-text-muted">
                {tag} <button onClick={() => removeTag(tag)}><X className="w-2.5 h-2.5" /></button>
              </span>
            ))}
            <div className="flex items-center gap-1">
              <input value={newTag} onChange={e => setNewTag(e.target.value)} onKeyDown={e => e.key === 'Enter' && addTag()} placeholder="+ tag" className="text-[10px] w-16 bg-transparent border border-border-subtle rounded px-1.5 py-0.5 text-text-muted focus:outline-none focus:border-primary" />
              <button onClick={addTag} className="text-[10px] text-primary"><Tag className="w-3 h-3" /></button>
            </div>
          </div>

          {/* Log action */}
          <div className="flex flex-wrap gap-2 items-end">
            <select value={actionType} onChange={e => setActionType(e.target.value)} className="bg-background border border-border-subtle rounded-lg px-2 py-1.5 text-xs text-text-body focus:outline-none focus:border-primary">
              {ACTION_OPTS.map(a => <option key={a.id} value={a.id}>{a.label} ({a.pts})</option>)}
            </select>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="Nota (opcional)" className="flex-1 min-w-24 bg-background border border-border-subtle rounded-lg px-2 py-1.5 text-xs text-text-body focus:outline-none focus:border-primary" />
            <button onClick={logAction} disabled={busy} className="btn-primary !px-3 !py-1.5 text-xs flex items-center gap-1" data-testid={`crm-log-action-${contact.contact_id}`}>
              {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Registrar'}
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}

export default function AgenteCRMPanel({ authFetch }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const apiFn = authFetch || ((url, opts) => fetch(url, { ...opts, credentials: 'include' }));

  const load = useCallback(async (followup = false) => {
    setLoading(true);
    try {
      const url = followup ? `${API}/crm-agent/contacts?needs_followup=true` : `${API}/crm-agent/contacts`;
      const res = await apiFn(url);
      setData(await res.json());
    } catch { toast.error('Error al cargar CRM'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runAgent = async () => {
    setRunning(true);
    try {
      const res = await apiFn(`${API}/crm-agent/run`, { method: 'POST' });
      const d = await res.json();
      toast.success(d.message || 'CRM sincronizado');
      await load();
    } catch { toast.error('Error'); }
    finally { setRunning(false); }
  };

  const logAction = async (contactId, actionType, notes) => {
    try {
      await apiFn(`${API}/crm-agent/contacts/${contactId}/action`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ action_type: actionType, notes }) });
      toast.success('Acción registrada');
      await load(filter === 'followup');
    } catch { toast.error('Error'); }
  };

  const updateTags = async (contactId, tags) => {
    try {
      await apiFn(`${API}/crm-agent/contacts/${contactId}/tags`, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ tags }) });
      await load(filter === 'followup');
    } catch { toast.error('Error'); }
  };

  const createContact = async (form) => {
    try {
      await apiFn(`${API}/crm-agent/contacts`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
      toast.success('Contacto creado');
      setShowModal(false);
      await load();
    } catch { toast.error('Error'); }
  };

  const contacts = (data?.contacts || []).filter(c => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (c.name || '').toLowerCase().includes(q) ||
           (c.phone || '').includes(q) ||
           (c.email || '').toLowerCase().includes(q) ||
           (c.country || '').toLowerCase().includes(q);
  });
  const stats = data?.stats || {};

  return (
    <div className="space-y-5" data-testid="agente-crm-panel">
      {showModal && <NewContactModal onClose={() => setShowModal(false)} onSave={createContact} />}

      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h3 className="text-text-heading font-heading text-lg font-semibold flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" /> Agente de CRM
          </h3>
          <p className="text-sm text-text-muted mt-0.5">Enriquecimiento automático de contactos + seguimientos pendientes.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => load()} disabled={loading} className="btn-secondary !px-3 !py-2"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></button>
          <button onClick={runAgent} disabled={running} className="btn-secondary !px-4 !py-2 text-sm flex items-center gap-1.5">
            {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
            Sincronizar pipeline
          </button>
          <button onClick={() => setShowModal(true)} className="btn-primary !px-4 !py-2 text-sm flex items-center gap-1.5">
            <Plus className="w-4 h-4" /> Nuevo contacto
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        {[
          { label: 'Total', value: stats.total ?? 0, color: 'text-primary' },
          { label: 'Seguimiento pendiente', value: stats.needs_followup ?? 0, color: 'text-amber-400' },
          { label: 'Contactos calientes', value: stats.hot ?? 0, color: 'text-emerald-400' },
        ].map(({ label, value, color }) => (
          <div key={label} className="glass-card p-4">
            <p className="text-[10px] text-text-muted uppercase tracking-wider mb-1">{label}</p>
            <p className={`text-2xl font-black ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Filter tabs + search */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border-subtle pb-2">
        <div className="flex gap-1">
          {[['all', 'Todos'], ['followup', 'Requieren seguimiento']].map(([id, label]) => (
            <button key={id} onClick={() => { setFilter(id); load(id === 'followup'); }}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${filter === id ? 'border-primary text-primary' : 'border-transparent text-text-muted hover:text-text-body'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted pointer-events-none" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar contacto…"
            className="pl-9 pr-3 py-1.5 text-sm bg-card border border-border-subtle rounded-xl text-text-body placeholder-text-muted focus:outline-none focus:border-primary"
            data-testid="crm-search-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        {loading ? (
          <div className="flex items-center justify-center py-10 text-text-muted"><Loader2 className="w-5 h-5 animate-spin mr-2" />Cargando…</div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-10 text-text-muted text-sm">Sin contactos. Ejecuta "Sincronizar pipeline" para importarlos automáticamente.</div>
        ) : (
          contacts.map(c => (
            <ContactRow key={c.contact_id} contact={c} onAction={logAction} onTagUpdate={updateTags}
              expanded={expandedId === c.contact_id} onToggle={() => setExpandedId(expandedId === c.contact_id ? null : c.contact_id)} />
          ))
        )}
      </div>
    </div>
  );
}
